import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Avatar,
  Dialog,
  DialogContent
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import FlightIcon from "@mui/icons-material/Flight";
import SportsRugbyIcon from '@mui/icons-material/SportsRugby';

import Ladder from "./pages/Ladder";
import MatchDetails from "./pages/MatchDetails";
import Results from "./pages/Results";

const API_URL = process.env.REACT_APP_API_URL;

export default function App() {
  const [page, setPage] = useState("home");
  const [nextGame, setNextGame] = useState(null);
  const [pastResults, setPastResults] = useState([]);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [countdown, setCountdown] = useState("");
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [weather, setWeather] = useState(null);

  const isGameLive = (game) => {
    if (!game) return false;

    const now = new Date();
    const start = new Date(game.game_date);
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);

    return now >= start && now < end;
  };

  useEffect(() => {
    const fetchNextOrLiveGame = async () => {
      try {
        let nextGameData = null;

        try {
          const liveRes = await fetch(`${API_URL}/live-game`);

          if (liveRes.ok) {
            const liveGame = await liveRes.json();

            if (liveGame && liveGame.match_number) {
              nextGameData = liveGame;
            }
          }
        } catch (err) {
          console.warn("Live game fetch failed:", err.message);
        }

        if (!nextGameData) {
          const nextRes = await fetch(`${API_URL}/next-game`);

          if (nextRes.ok) {
            nextGameData = await nextRes.json();
          }
        }

        setNextGame(nextGameData || null);

        const [pastRes, upcomingRes] = await Promise.all([
          fetch(`${API_URL}/past-results`).then((r) => r.json()),
          fetch(`${API_URL}/upcoming-games`).then((r) => r.json()),
        ]);

        setPastResults(pastRes || []);
        setUpcomingGames(upcomingRes || []);
      } catch (err) {
        console.error("Error fetching games:", err);
      }
    };

    fetchNextOrLiveGame();
  }, []);

  useEffect(() => {
    if (!nextGame) return;

    const updateCountdown = () => {
      const now = new Date();
      const gameTime = new Date(nextGame.game_date);

      const diff = gameTime - now;

      if (diff <= 0) {
        setCountdown("Game Started");
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);

      setCountdown(`${days}d ${hours}h ${minutes}m`);
    };

    updateCountdown();

    const interval = setInterval(updateCountdown, 60000);

    const fetchWeather = async () => {
      try {
        const res = await fetch(
          `${API_URL}/get-weather?stadium=${encodeURIComponent(
            nextGame.location
          )}`
        );

        const data = await res.json();

        const gameTime = new Date(nextGame.game_date);

        const forecast = data.list.reduce((closest, item) => {
          const itemTime = new Date(item.dt * 1000);

          return Math.abs(itemTime - gameTime) <
            Math.abs(new Date(closest.dt * 1000) - gameTime)
            ? item
            : closest;
        }, data.list[0]);

        setWeather({
          temp: Math.round(forecast.main.temp),
          description: forecast.weather[0].main,
          icon: `http://openweathermap.org/img/wn/${forecast.weather[0].icon}.png`,
        });
      } catch (err) {
        console.error("Failed to fetch weather:", err);
      }
    };

    fetchWeather();

    return () => clearInterval(interval);
  }, [nextGame]);

  const seasonStats = pastResults.reduce(
    (acc, game) => {
      const warriorsHome = game.home_team === "Warriors";

      const warriorsScore = warriorsHome
        ? game.home_team_score
        : game.away_team_score;

      const opponentScore = warriorsHome
        ? game.away_team_score
        : game.home_team_score;

      if (warriorsScore > opponentScore) acc.wins++;
      else if (warriorsScore < opponentScore) acc.losses++;
      else acc.draws++;

      return acc;
    },
    { wins: 0, losses: 0, draws: 0 }
  );

  const filteredUpcomingGames = upcomingGames.filter(
    (game) => nextGame && game.match_number !== nextGame.match_number
  );

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const getResultColor = (game) => {
    const warriorsScore =
      game.home_team === "Warriors"
        ? game.home_team_score
        : game.away_team_score;

    const opponentScore =
      game.home_team === "Warriors"
        ? game.away_team_score
        : game.home_team_score;

    if (warriorsScore > opponentScore) return "#00E676";
    if (warriorsScore < opponentScore) return "#FF5252";

    return "#A7B3C2";
  };

  const nextCardStyle = {
    borderRadius: 5,
    minHeight: 240,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    width: "100%",
    background: "linear-gradient(145deg, #0B2A57, #123C73)",
    color: "#F5F7FA",
    border: "1px solid rgba(255,255,255,0.08)",
    overflow: "hidden",
  };

  const pastCardStyle = {
    borderRadius: 4,
    minHeight: 140,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
    width: "100%",
    background: "#0B2A57",
    color: "#F5F7FA",
    border: "1px solid rgba(255,255,255,0.08)",
    transition: "0.2s ease",
    cursor: "pointer",

    "&:hover": {
      transform: "translateY(-4px)",
      background: "#123C73",
    },
  };

  return (
    <>
      <Box
        sx={{
          py: 4,
          pb: 12,
          paddingX: 2,
          minHeight: "100vh",
          background:
            "linear-gradient(180deg, #041E42 0%, #062B5B 100%)",
        }}
      >
        {page === "home" && nextGame && (
          <>
            {/* Next Game */}
            <Card sx={{ ...nextCardStyle, mb: 4 }}>
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    mb: 3,
                    letterSpacing: 1,
                  }}
                >
                  NEXT GAME
                </Typography>

                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, mb: 1 }}
                >
                  Warriors vs {nextGame.opponent}
                </Typography>

                <Typography sx={{ color: "#A7B3C2", mb: 1 }}>
                  {formatDate(nextGame.game_date)} at{" "}
                  {formatTime(nextGame.game_date)}
                </Typography>

                <Typography sx={{ color: "#A7B3C2" }}>
                  {nextGame.location}
                </Typography>

                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {isGameLive(nextGame) ? (
                    <Chip
                      label="LIVE"
                      sx={{
                        backgroundColor: "#FF1744",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  ) : (
                    <>
                      <Typography sx={{ color: "#A7B3C2" }}>
                        Kickoff in
                      </Typography>

                      <Typography
                        variant="h5"
                        sx={{
                          color: "#00E676",
                          fontWeight: 700,
                        }}
                      >
                        {countdown}
                      </Typography>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 54,
                      height: 54,
                      bgcolor: "#00A651",
                    }}
                  >
                    {nextGame.home_team === "Warriors" ? (
                      <HomeIcon />
                    ) : (
                      <FlightIcon />
                    )}
                  </Avatar>

                  {weather && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 54,
                          height: 54,
                          borderRadius: "50%",
                          backgroundColor: "#123C73",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={weather.icon}
                          alt={weather.description}
                          width={42}
                          height={42}
                        />
                      </Box>

                      <Typography sx={{ color: "#F5F7FA" }}>
                        {weather.temp}°C · {weather.description}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

        {/* Season Record */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 5,
            height: 70,
            background: "linear-gradient(145deg, #0B2A57, #123C73)",
            color: "#F5F7FA",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                gap: 3,
              }}
            >
              <Chip
                label={`WINS: ${seasonStats.wins}`}
                sx={{
                  flex: 1,
                  backgroundColor: "#00A651",
                  color: "white",
                  fontWeight: "bold",
                }}
              />

              <Chip
                label={`LOSSES: ${seasonStats.losses}`}
                sx={{
                  flex: 1,
                  backgroundColor: "#FF5252",
                  color: "white",
                  fontWeight: "bold",
                }}
              />

              <Chip
                label={`TIE: ${seasonStats.draws}`}
                sx={{
                  flex: 1,
                  backgroundColor: "#546E7A",
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            </Box>
          </CardContent>
        </Card>

            {/* Upcoming Games */}
            {filteredUpcomingGames.length > 0 && (
              <Accordion
                sx={{
                  mb: 4,
                  borderRadius: "18px !important",
                  overflow: "hidden",
                  background: "#0B2A57",
                  color: "#F5F7FA",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",

                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}>
                  <Typography variant="h6">
                    Future Games
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <List>
                    {filteredUpcomingGames.map((game) => (
                      <ListItem
                        key={game.match_number}
                        sx={{
                          borderBottom:
                            "1px solid rgba(255,255,255,0.06)",
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography sx={{ color: "#F5F7FA" }}>
                              {formatDate(game.game_date)} at{" "}
                              {formatTime(game.game_date)} - vs{" "}
                              {game.opponent}
                            </Typography>
                          }
                          secondary={
                            <Typography sx={{ color: "#A7B3C2" }}>
                              {game.location}
                            </Typography>
                          }
                        />

                        <Avatar
                          sx={{
                            ml: 2,
                            width: 34,
                            height: 34,
                            bgcolor: "#00A651",
                          }}
                        >
                          {game.home_team === "Warriors" ? (
                            <HomeIcon fontSize="small" />
                          ) : (
                            <FlightIcon fontSize="small" />
                          )}
                        </Avatar>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Past Results */}
            <Typography
              variant="h5"
              sx={{
                color: "#F5F7FA",
                fontWeight: 700,
                mb: 3,
              }}
            >
              PAST RESULTS
            </Typography>

            <Grid
            container
            spacing={3}
            justifyContent="center"
            alignItems="stretch"
          >
            {pastResults.map((game) => {
              const warriorsHome = game.home_team === "Warriors";

              return (
                <Grid
                  item
                  xs={12}
                  sm={10}
                  md={6}
                  lg={4}
                  key={game.match_number}
                  onClick={() => setSelectedMatch(game)}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Card
                    sx={{
                      ...pastCardStyle,
                      width: "100%",
                      maxWidth: 420,
                    }}
                  >
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            textAlign: "left",
                          }}
                        >
                          Warriors vs{" "}
                          {warriorsHome
                            ? game.away_team
                            : game.home_team}
                        </Typography>

                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            marginLeft: 2,
                            bgcolor: "#00A651",
                          }}
                        >
                          {warriorsHome ? (
                            <HomeIcon fontSize="small" />
                          ) : (
                            <FlightIcon fontSize="small" />
                          )}
                        </Avatar>
                      </Box>

                      <Typography
                        sx={{
                          color: "#A7B3C2",
                          mb: 2,
                          textAlign: "left",
                        }}
                      >
                        {formatDate(game.game_date)} at{" "}
                        {formatTime(game.game_date)}
                      </Typography>

                      <Typography
                        sx={{
                          color: getResultColor(game),
                          fontWeight: 700,
                          fontSize: "1.4rem",
                          textAlign: "center",
                        }}
                      >
                        {game.home_team_score} -{" "}
                        {game.away_team_score}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          </>
        )}

        <Dialog
          open={!!selectedMatch}
          onClose={() => setSelectedMatch(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              height: "50vh",
              background: "transparent",
              boxShadow: "none",
              overflow: "visible",
            },
          }}
        >
          <DialogContent
            sx={{
              p: {
                xs: 1,
                sm: 2,
              },
              background: "transparent",
            }}
          >
            {selectedMatch && (
              <MatchDetails
                game={selectedMatch}
                onBack={() => setSelectedMatch(null)}
              />
            )}
          </DialogContent>
        </Dialog>

        {page === "ladder" && <Ladder />}
        
        {page === "results" && <Results />}
      </Box>

      {/* Bottom Navigation */}
      <Paper
        sx={{
          position: "fixed",
          bottom: 16,
          left: 16,
          right: 16,
          borderRadius: 5,
          overflow: "hidden",
          background: "#03152E",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
        elevation={10}
      >
        <BottomNavigation
          value={page}
          onChange={(e, val) => setPage(val)}
          showLabels
          sx={{
            height: 75,
            background: "#03152E",

            "& .MuiBottomNavigationAction-root": {
              color: "#A7B3C2",
            },

            "& .Mui-selected": {
              color: "#00A651",
            },
          }}
        >
          <BottomNavigationAction
            label="Home"
            value="home"
            icon={<HomeIcon />}
          />

          <BottomNavigationAction
            label="Ladder"
            value="ladder"
            icon={<LeaderboardIcon />}
          />

          <BottomNavigationAction
            label="All Results"
            value="results"
            icon={<SportsRugbyIcon />}
          />
        </BottomNavigation>
      </Paper>
    </>
  );
}